import { CheckCircle2, Camera, Mail, CalendarPlus } from "lucide-react";
import { bookingIcsUrl } from "@/lib/api";

const fmtTimeLabel = (hhmm) => {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map((n) => parseInt(n, 10));
  const period = h >= 12 ? "PM" : "AM";
  const hh = ((h + 11) % 12) + 1;
  return `${hh}:${String(m).padStart(2, "0")} ${period}`;
};

export default function SuccessScreen({ booking, onBookAnother, businessName }) {
  return (
    <div className="pp-card p-6 md:p-10 text-center pp-step-fade" data-testid="success-screen">
      <div
        className="mx-auto w-14 h-14 rounded-full flex items-center justify-center mb-4"
        style={{ background: "var(--pp-success-bg)" }}
      >
        <CheckCircle2 className="w-8 h-8" style={{ color: "var(--pp-success)" }} />
      </div>
      <h2 className="text-2xl md:text-3xl font-semibold mb-1" data-testid="success-title">
        You're booked!
      </h2>
      <p className="text-sm mb-6" style={{ color: "var(--pp-muted)" }}>
        Thanks {booking.owner_first_name}. A confirmation email has been sent — please screenshot or note your details below.
      </p>

      <div
        className="text-left mx-auto max-w-md px-5 py-4 mb-6"
        style={{ background: "var(--pp-cream-soft)", borderRadius: "var(--pp-radius)" }}
      >
        <div className="flex items-center justify-between text-sm py-1">
          <span style={{ color: "var(--pp-muted)" }}>Booking ID</span>
          <span className="font-mono font-semibold" data-testid="success-booking-id">{booking.booking_id}</span>
        </div>
        <div className="flex items-center justify-between text-sm py-1">
          <span style={{ color: "var(--pp-muted)" }}>Confirmation code</span>
          <span
            className="font-mono font-semibold tracking-widest"
            style={{ color: "var(--pp-indigo)" }}
            data-testid="success-confirmation-code"
          >
            {booking.confirmation_code}
          </span>
        </div>
        <div className="flex items-center justify-between text-sm py-1">
          <span style={{ color: "var(--pp-muted)" }}>For</span>
          <span data-testid="success-pet-name">{booking.pet_name}</span>
        </div>
        <div className="flex items-center justify-between text-sm py-1">
          <span style={{ color: "var(--pp-muted)" }}>Date</span>
          <span data-testid="success-date">
            {new Date(booking.appointment_date + "T00:00:00").toLocaleDateString(undefined, {
              weekday: "long", month: "long", day: "numeric", year: "numeric",
            })}
          </span>
        </div>
        <div className="flex items-center justify-between text-sm py-1">
          <span style={{ color: "var(--pp-muted)" }}>Time</span>
          <span data-testid="success-time">{fmtTimeLabel(booking.appointment_time)}</span>
        </div>
      </div>

      <div className="flex flex-col gap-2 text-xs mb-6" style={{ color: "var(--pp-muted)" }}>
        <span className="inline-flex items-center justify-center gap-2">
          <Camera className="w-3.5 h-3.5" />
          Tip: take a screenshot or save your confirmation code for your records.
        </span>
        <span className="inline-flex items-center justify-center gap-2">
          <Mail className="w-3.5 h-3.5" />
          A copy was sent to {booking.owner_email}.
        </span>
      </div>

      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
        <a
          href={bookingIcsUrl(booking.confirmation_code)}
          download={`${booking.booking_id}.ics`}
          className="pp-btn-ghost"
          data-testid="add-to-calendar-btn"
        >
          <CalendarPlus className="w-4 h-4" />
          Add to calendar
        </a>
        <button
          type="button"
          onClick={onBookAnother}
          className="pp-btn-primary"
          data-testid="book-another-btn"
        >
          Book another appointment
        </button>
      </div>
      <p className="text-xs mt-4" style={{ color: "var(--pp-muted)" }}>
        Need to cancel or reschedule? Please call {businessName} directly.
      </p>
    </div>
  );
}
